import { voidFunction } from '@avantia/client-and-server-utilities';
import { HpError } from '@avantia/logging-and-errors';
import { createControlId, HpButtonStyles, HpDependentAction } from '@avantia/questionset-model';
import { BaseSyntheticEvent, useEffect } from 'react';
import { HpDynamicButtonClickPayload } from '../../../engineClient/actions/dynamicButtonClickActionTypes';
import { triggerModalActionEvent } from '../../../engineClient/actions/eventActions';
import { ButtonControl, HpButtonListControlProps } from '../../../engineClient/components/formControls/standard';
import { HtmlWidget } from '../../../engineClient/components/widgets/htmlWidget';
import { showConfirmModal } from '../../../engineClient/components/widgets/modalWidget';
import {
  HpClientQuestion,
  HpClientQuestionData,
  HpClientStandardButtonTextAndStyle,
  HpClientStandardInputTextAndStyle,
  HpOnMouseEvent
} from '../../../engineClient/types';
import { HpQuestionSetPageProps } from '../../../engineClient/types/questionSetPagePropTypes';

interface ModalCustomControlProps extends HpButtonListControlProps {
  customData: ModalCustomControlCustomData;
}

interface ModalCustomControlCustomData {
  title: string;
  triggerModalButtonClass: string;
  confirmButtonPosition: ModalButtonPositionTypes;
  confirmButtonStyle: ModalButtonStyleTypes;
  cancelButtonStyle: ModalButtonStyleTypes;
  body?: string;
  bodyHtml?: string;
  modalCssClass?: string;
  validationActions: HpDependentAction[];
}

type ModalButtonPositionTypes = 'top' | 'bottom';
type ModalButtonTypes = 'true' | 'false' | 'cancel';
type ModalButtonStyleTypes = 'cancel' | 'confirm';
const modalButtonTypes: ModalButtonTypes[] = ['true', 'false', 'cancel'];
const confirmButtonClass = 'primary hp-next-section';
const cancelButtonClass = 'dark hp-next-section hp-previous-section';

export interface HpModalQuestionUI {
  isModalVisible?: boolean;
}

export function ModalCustomControl(props: ModalCustomControlProps): JSX.Element | null {
  const { question, id, onClick, formatType, pageProps } = props;
  const { data } = question;
  const { customData, textAndStyle: dataTextAndStyle } = data as HpClientQuestionData;
  const textAndStyle = dataTextAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const modalControlCustomData = customData as unknown as ModalCustomControlCustomData;
  const { questionText } = textAndStyle;
  const { triggerModalButtonClass, validationActions } = modalControlCustomData;

  let onClickHandler: (event: BaseSyntheticEvent) => void;
  const { displayState } = pageProps;
  const { questionsUI } = displayState;
  const modalQuestionUI = questionsUI[id] as HpModalQuestionUI;
  const isModalVisible = modalQuestionUI.isModalVisible;
  onClickHandler = () => voidFunction();

  if (validationActions) {
    useEffect(() => {
      if (isModalVisible) {
        showModal({ modalControlCustomData, question, id, onClick });
      }
    }, [isModalVisible]);

    onClickHandler = createModalValidationHandler(question, pageProps, validationActions);
  } else {
    onClickHandler = createCustomModalEvent({
      question,
      id,
      modalControlCustomData,
      onClick
    });
  }

  const buttonProps = {
    ...props,
    type: undefined,
    onClick: onClickHandler,
    buttonText: questionText as string,
    data: {
      ...data,
      textAndStyle: {
        standard: {
          ...textAndStyle,
          buttonText: questionText,
          buttonStyle: triggerModalButtonClass as HpButtonStyles // TODO AL - This should be a custom button, maybe with a custom dependent action.
        } as HpClientStandardButtonTextAndStyle
      }
    }
  };

  return <ButtonControl {...buttonProps}></ButtonControl>;
}

interface ShowModalProps {
  id: string;
  modalControlCustomData: ModalCustomControlCustomData;
  onClick: HpOnMouseEvent;
  question: HpClientQuestion;
}

function createCustomModalEvent({
  question,
  id,
  modalControlCustomData,
  onClick
}: ShowModalProps): (event: BaseSyntheticEvent) => void {
  return (event) => {
    event.stopPropagation();
    triggerModalActionEvent({ question, getEvent: () => event, buttonType: 'show' });
    showModal({ modalControlCustomData, question, id, onClick });
  };
}

function showModal({ id, modalControlCustomData, onClick, question }: ShowModalProps): void {
  const {
    title,
    body = '',
    confirmButtonPosition = 'top',
    confirmButtonStyle,
    cancelButtonStyle,
    bodyHtml,
    modalCssClass
  } = modalControlCustomData;
  if (!body && !bodyHtml) {
    throw new HpError(`Question ${id} should have a body or bodyHtml.`);
  }

  const modalClass = `hp-modal-dialog ${modalCssClass}`.trim();
  const modalBody = bodyHtml ? <HtmlWidget html={bodyHtml} /> : body;

  showConfirmModal({
    question,
    id,
    modalClass,
    modalHeaderClass: 'hp-heading-control',
    modalCopyText: {
      title,
      body: modalBody
    },
    onConfirmClick: onClick,
    customButtons: ({ handleConfirm, handleCancel }) => {
      return (
        <>
          {createCustomModalButtons({
            handleConfirm,
            handleCancel,
            question,
            id,
            confirmButtonPosition,
            confirmButtonStyle,
            cancelButtonStyle
          })}
        </>
      );
    }
  });
}

interface CreateCustomModalButtonsProps {
  handleConfirm: (event: BaseSyntheticEvent) => void;
  handleCancel: (event: BaseSyntheticEvent) => void;
  question: HpClientQuestion;
  id: string;
  confirmButtonPosition: ModalButtonPositionTypes;
  confirmButtonStyle?: ModalButtonStyleTypes;
  cancelButtonStyle?: ModalButtonStyleTypes;
}

function createCustomModalButtons({
  handleConfirm,
  handleCancel,
  question,
  id,
  confirmButtonPosition,
  confirmButtonStyle,
  cancelButtonStyle
}: CreateCustomModalButtonsProps) {
  const { data } = question;
  const { lookup } = data as HpClientQuestionData;
  // TODO AL - Perhaps put this in customData instead of lookup.
  const lookupKeys = Object.keys(lookup);

  let updatedConfirmButtonClass = '';
  let updatedCancelButtonClass = '';

  if (confirmButtonStyle === 'cancel') {
    updatedConfirmButtonClass = cancelButtonClass;
  }

  if (cancelButtonStyle === 'confirm') {
    updatedCancelButtonClass = confirmButtonClass;
  }

  const modalButtons = lookupKeys.map((key) => createYesNoModalButton(key as ModalButtonTypes));
  return <div className="flex-column flex-fill">{modalButtons}</div>;

  function createYesNoModalButton(buttonKey: 'true' | 'false' | 'cancel') {
    if (confirmButtonPosition === 'bottom' && buttonKey !== 'cancel') {
      buttonKey = buttonKey === 'false' ? 'true' : 'false';
    }

    const optionKey = createControlId({ questionId: id, key: buttonKey });
    let style: string | null = '';
    let handleClick: (event: BaseSyntheticEvent) => void;
    switch (buttonKey) {
      case 'true':
        style = updatedConfirmButtonClass || confirmButtonClass;
        handleClick = (event: BaseSyntheticEvent) => {
          triggerModalActionEvent({ question, getEvent: () => event, buttonType: 'confirm' });
          return handleConfirm(event);
        };

        break;
      case 'false':
        // TODO AL - Button classes are too complicated, use style guide classes or create new one if there isn't any.
        style = updatedCancelButtonClass || cancelButtonClass;
        handleClick = (event: BaseSyntheticEvent) => {
          triggerModalActionEvent({ question, getEvent: () => event, buttonType: 'cancel' });
          return handleConfirm(event);
        };

        break;
      case 'cancel':
        style = updatedCancelButtonClass || cancelButtonClass;
        handleClick = handleCancel;
        break;
      default:
        throw new HpError(
          `Question ${id} contains an unknown lookup key: ${buttonKey}. Valid keys are: ${modalButtonTypes.map(
            (key) => `${key}`
          )}`
        );
    }

    const lookupButton = lookup[buttonKey];

    return (
      <div className="pt-3" key={`${id}-${buttonKey}`}>
        <button
          key={createControlId({ questionId: id, key: optionKey })}
          id={optionKey}
          name={buttonKey}
          onClick={handleClick}
          type="button"
          className={`animated btn btn-${style} animated pulse hp-full-width-button`}
          tabIndex={0}
        >
          {lookupButton ? lookupButton.text : undefined}
        </button>
      </div>
    );
  }
}

function createModalValidationHandler(
  question: HpClientQuestion,
  pageProps: HpQuestionSetPageProps,
  validationActions: HpDependentAction[]
): (event) => void {
  const { data, questionId } = question;
  const questionWithValidationActions = {
    ...question,
    data: { ...data, dependentActions: [...validationActions, { action: 'toggle-modal' }] }
  };
  return (event) => {
    const { dynamicButtonClick } = pageProps;
    dynamicButtonClick({
      formName: 'QuestionSet',
      question: questionWithValidationActions,
      questionId,
      pageProps: {
        ...pageProps,
        questions: {
          ...pageProps.questions,
          [questionId]: questionWithValidationActions
        }
      },
      event,
      getEvent: () => event
    } as unknown as HpDynamicButtonClickPayload);
  };
}
